import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface KanbanColumn<T> {
  id: string;
  title: ReactNode;
  items: T[];
  accent?: 'blue' | 'amber' | 'green' | 'zinc' | 'red';
}

export interface KanbanBoardProps<T> {
  columns: KanbanColumn<T>[];
  renderItem: (item: T) => ReactNode;
  itemKey: (item: T) => string;
  onItemClick?: (item: T) => void;
  className?: string;
}

const accentClasses: Record<NonNullable<KanbanColumn<unknown>['accent']>, string> = {
  blue: 'border-t-blue-500',
  amber: 'border-t-amber-500',
  green: 'border-t-green-500',
  zinc: 'border-t-zinc-400',
  red: 'border-t-red-500',
};

export function KanbanBoard<T>({
  columns,
  renderItem,
  itemKey,
  onItemClick,
  className,
}: KanbanBoardProps<T>) {
  return (
    <div className={cn('flex h-full gap-3 overflow-x-auto p-1', className)}>
      {columns.map((col) => (
        <section
          key={col.id}
          className={cn(
            'flex w-72 min-w-[18rem] flex-col rounded-lg bg-zinc-100/60 border-t-4',
            accentClasses[col.accent ?? 'zinc'],
          )}
        >
          <header className="flex items-center justify-between px-3 py-2">
            <h3 className="text-sm font-semibold text-zinc-700">{col.title}</h3>
            <span className="text-xs text-zinc-500">{col.items.length}</span>
          </header>
          <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
            {col.items.map((item) => (
              <div
                key={itemKey(item)}
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                className={cn(
                  'rounded-md bg-white p-3 shadow-sm ring-1 ring-zinc-200 transition',
                  onItemClick && 'cursor-pointer hover:ring-zinc-300',
                )}
              >
                {renderItem(item)}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
